import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useMemo, useState } from "react";
import { Play, Star, Calendar, Clock, Film, Heart, Bookmark, Share2, Check, Download, Eye, MessageSquare, Flame, Sparkles, Tv } from "lucide-react";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { PosterCard } from "@/components/rawan/PosterCard";
import { RawanPlayer } from "@/components/rawan/RawanPlayer";
import { AdSlot } from "@/components/rawan/AdSlot";
import { getItemBySlug, useAllItems } from "@/lib/library-store";
import { useEpisodes } from "@/lib/episodes-store";
import { getMergedSeasonsForSeries as getSeasonsForSeries, useEpisodeOverrides } from "@/lib/episode-overrides";
import type { ContentSeason } from "@/lib/content/schema";
import { useTheme } from "@/lib/theme-context";
import type { LanguageCode } from "@/lib/theme-context";
import { matchesCountry, getCountryByCode } from "@/lib/countries";
import { encryptUrl } from "@/lib/safelink-crypto";
import { isFavorite, toggleFavorite, useFavorites } from "@/lib/favorites";
import { isOnWatchlist, toggleWatchlist, useWatchlist } from "@/lib/watchlist";
import { CommentsSection } from "@/components/rawan/CommentsSection";
import { ArticleRenderer } from "@/components/rawan/ArticleRenderer";
import { ArticleSidebar } from "@/components/rawan/ArticleSidebar";
import { useEffect } from "react";
import { bumpViews, useViews, useCommentsCount } from "@/lib/firebase-data";
import { useIsHandheld } from "@/hooks/use-device";
import {
  OG_LOCALE, SUPPORTED_LANGS, UI_T, buildHreflangLinks,
  formatDate, pickGenres, pickOverview, pickTitle, withLang, pickDuration, pickSeasonInfo, pickStatus,
} from "@/lib/i18n";
import { localizeEpisodeTitle } from "@/lib/episode-title";


export const Route = createFileRoute("/series/$slug")({
  validateSearch: (s: Record<string, unknown>) =>
    z.object({
      lang: z.enum(["ar", "fr", "en"]).optional(),
      country: z.string().optional(),
    }).parse(s),
  loaderDeps: ({ search }) => ({ lang: search.lang ?? "ar" }),
  loader: ({ params, deps }) => {
    const series = getItemBySlug(params.slug);
    return {
      series: series && series.type === "series" ? series : null,
      slug: params.slug,
      lang: deps.lang as LanguageCode,
    };
  },
  head: ({ loaderData }) => {
    const s = loaderData?.series;
    if (!s) return {};
    const lang: LanguageCode = (loaderData?.lang as LanguageCode) || "ar";
    const c = s.country ? getCountryByCode(s.country) : undefined;
    const title = pickTitle(s, lang);
    const overview = pickOverview(s, lang);
    const T = UI_T[lang];
    const titleSuffix = c ? ` — ${T.series} ${lang === "ar" ? c.ar : lang === "fr" ? c.fr : c.en}` : ` — ${T.series}`;
    const basePath = `/series/${s.slug}`;
    const canonical = withLang(basePath, lang);
    return {
      meta: [
        { title: `${title}${titleSuffix} — Rawan UI` },
        { name: "description", content: overview.slice(0, 155) },
        { name: "keywords", content: [...pickGenres(s, lang), s.titleEn, title].join(", ") },
        { property: "og:title", content: `${title}${titleSuffix}` },
        { property: "og:description", content: overview.slice(0, 200) },
        { property: "og:image", content: s.poster },
        { property: "og:image:secure_url", content: s.poster },
        { property: "og:image:alt", content: `${title} — ${T.series}` },
        { property: "og:image:width", content: "500" },
        { property: "og:image:height", content: "750" },
        { property: "og:type", content: "video.tv_show" },
        { property: "og:url", content: canonical },
        { property: "og:locale", content: OG_LOCALE[lang] },
        ...SUPPORTED_LANGS.filter((l) => l !== lang).map((l) => ({
          property: "og:locale:alternate", content: OG_LOCALE[l],
        })),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${title}${titleSuffix}` },
        { name: "twitter:description", content: overview.slice(0, 200) },
        { name: "twitter:image", content: s.poster },
        { name: "twitter:image:alt", content: title },
      ],
      links: [
        { rel: "canonical", href: canonical },
        ...buildHreflangLinks(basePath),
      ],
      scripts: [{
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "TVSeries",
          name: title,
          alternateName: [s.title, s.titleEn, s.titleFr].filter(Boolean),
          image: s.poster,
          description: overview,
          datePublished: s.firstAirDate || (s.year ? String(s.year) : undefined),
          inLanguage: lang,
          genre: pickGenres(s, lang),
          numberOfSeasons: undefined,
          ...(pickStatus(s, lang) ? { creativeWorkStatus: pickStatus(s, lang) } : {}),
          ...(c ? { countryOfOrigin: { "@type": "Country", name: c.en } } : {}),
          aggregateRating: { "@type": "AggregateRating", ratingValue: s.rating, bestRating: 10, ratingCount: 1000 },
          actor: s.cast.map((n) => ({ "@type": "Person", name: n })),
          director: s.director ? { "@type": "Person", name: s.director } : undefined,
        }),
      }],
    };
  },
  component: SeriesDetail,
  notFoundComponent: () => (
    <div className="min-h-dvh grid place-items-center px-4">
      <div className="text-center">
        <h1 className="text-3xl font-bold mb-4">المسلسل غير موجود / Series not found / Série introuvable</h1>
        <Link to="/" className="btn-primary inline-flex">العودة للرئيسية / Home / Accueil</Link>
      </div>
    </div>
  ),
  errorComponent: ({ error }) => {
    if (typeof window !== "undefined") console.error("[series route]", error);
    return (
      <div className="min-h-dvh flex flex-col">
        <main className="flex-1 grid place-items-center px-4 py-16">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-extrabold mb-2">تعذّر تحميل صفحة المسلسل / Could not load series page / Impossible de charger la page de la série</h1>
            <p className="text-sm text-muted-foreground mb-6">جرّب التحديث، أو عد للرئيسية وحاول مجدداً. / Try refreshing or return home and try again. / Essayez d’actualiser ou revenez à l’accueil.</p>
            <div className="flex justify-center gap-2 flex-wrap">
              <button onClick={() => window.location.reload()} className="btn-primary inline-flex">إعادة المحاولة / Retry / Réessayer</button>
              <Link to="/" className="px-5 py-2.5 rounded-lg border border-border hover:bg-accent">الرئيسية / Home / Accueil</Link>
            </div>
          </div>
        </main>
      </div>
    );
  },
});

function SeriesDetail() {
  // (helper defined below at module scope)
  const { series: initial, slug, lang: routeLang } = Route.useLoaderData();
  const search = Route.useSearch();
  const allItems = useAllItems();
  const series = initial ?? allItems.find((m) => m.slug === slug && m.type === "series");
  const { language } = useTheme();
  const navigate = useNavigate();
  const isHandheld = useIsHandheld();
  const lang: LanguageCode = (search.lang as LanguageCode) ?? routeLang ?? language ?? "ar";
  const UI = UI_T[lang];
  const t = (ar: string, en: string, fr?: string) => (lang === "ar" ? ar : lang === "fr" ? (fr ?? en) : en);

  // Scroll to #watch or #article anchor on mount (e.g. from Hero CTAs)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash.replace("#", "");
    if (hash !== "watch" && hash !== "article") return;
    const tries = [80, 250, 600, 1200];
    const timers = tries.map((ms) =>
      window.setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, ms),
    );
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, []);


  async function openSafe(url: string) {
    try {
      const token = await encryptUrl(url);
      navigate({ to: "/safelink", search: { t: token, d: 1 } as any });
    } catch {
      navigate({ to: "/safelink", search: { url } as any });
    }
  }
  const [trailerOpen, setTrailerOpen] = useState(false);
  useEffect(() => {
    if (series?.slug) bumpViews(series.slug);
  }, [series?.slug]);
  // Sync ?lang= with global header language so share URLs/OG reflect current locale.
  useEffect(() => {
    if (language && search.lang !== language) {
      navigate({ search: { ...(search as any), lang: language } as any, replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const episodes = useEpisodes(series?.slug);
  useEpisodeOverrides();
  const contentSeasons: ContentSeason[] = useMemo(
    () => (series ? getSeasonsForSeries(series.slug) : []),
    [series?.slug],
  );
  // Unified episode list: prefer admin-added (localStorage) — fall back to content JSON.
  type DisplayEp = { id: string; season: number; number: number; title: string; thumbnail?: string; url?: string };

  const displayEpisodes: DisplayEp[] = useMemo(() => {
    if (episodes.length > 0) {
      return episodes.map((e) => ({
        id: e.id, season: e.season, number: e.number, title: e.title, thumbnail: e.thumbnail, url: e.url,
      }));
    }
    const out: DisplayEp[] = [];
    for (const s of contentSeasons) {
      for (const e of s.episodes) {
        out.push({
          id: `${s.season}-${e.episode_number}`,
          season: s.season,
          number: e.episode_number,
          title: localizeEpisodeTitle(e.title, lang),
          thumbnail: e.thumbnail,
        });
      }
    }

    return out;
  }, [episodes, contentSeasons]);
  const seasons = useMemo(() => {
    const set = new Set(displayEpisodes.map((e) => e.season));
    return Array.from(set).sort((a, b) => a - b);
  }, [displayEpisodes]);
  const firstEpisode = useMemo(() => {
    if (episodes.length > 0) {
      const sorted = [...episodes].sort((a, b) => a.season - b.season || a.number - b.number);
      return { season: sorted[0].season, number: sorted[0].number };
    }
    const s = contentSeasons[0];
    const e = s?.episodes[0];
    return s && e ? { season: s.season, number: e.episode_number } : undefined;
  }, [episodes, contentSeasons]);
  const [activeSeason, setActiveSeason] = useState<number | null>(null);
  const currentSeason = activeSeason ?? seasons[0] ?? 1;
  const epsThisSeason = displayEpisodes.filter((e) => e.season === currentSeason);

  // Episode pagination: 10 per page, with prev/next + numbered pages.
  const EPS_PER_PAGE = 10;
  const [epPage, setEpPage] = useState(1);
  // Reset pagination when the season OR the underlying series changes.
  useEffect(() => { setEpPage(1); }, [currentSeason, series?.slug]);
  const totalEpPages = Math.max(1, Math.ceil(epsThisSeason.length / EPS_PER_PAGE));
  const safeEpPage = Math.min(Math.max(1, epPage), totalEpPages);
  const pagedEps = epsThisSeason.slice((safeEpPage - 1) * EPS_PER_PAGE, safeEpPage * EPS_PER_PAGE);
  const pageNumbers = buildPageNumbers(safeEpPage, totalEpPages);
  function goToEpPage(p: number) {
    const next = Math.min(Math.max(1, p), totalEpPages);
    setEpPage(next);
    if (typeof window !== "undefined") {
      const el = document.getElementById("watch");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  if (!series) {
    return (
      <div className="min-h-dvh flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-extrabold">{t("المسلسل غير موجود", "Series not found", "Série introuvable")}</h1>
          <Link to="/" className="btn-primary inline-flex mt-6">{t("\u0627\u0644\u0631\u0626\u064a\u0633\u064a\u0629", "Home", "Accueil")}</Link>
        </main>
        <Footer />
      </div>
    );
  }
  const related = allItems.filter((m) => m.type === "series" && m.id !== series.id).slice(0, 6);
  const sameCountry = series.country
    ? allItems
        .filter((m) => m.type === "series" && m.id !== series.id && matchesCountry(m.country, series.country!))
        .slice(0, 6)
    : [];
  const countryEntry = series.country ? getCountryByCode(series.country) : undefined;
  const article = (series as { article?: import("@/lib/article-types").Article }).article ?? [];

  return (
    <div className="min-h-dvh flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative">
          <div className="absolute inset-0 overflow-hidden">
            <img
              src={series.backdrop}
              alt=""
              aria-hidden
              loading="lazy"
              decoding="async"
              onError={(e) => {
                const el = e.currentTarget;
                if (series.poster && el.src !== series.poster) {
                  el.src = series.poster;
                } else {
                  el.style.display = "none";
                }
              }}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/85 to-black/95" />
            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-background to-transparent" />
          </div>
          <div className="relative container mx-auto px-4 pt-28 pb-8">
            <div className="grid md:grid-cols-[240px_1fr] gap-6 items-start md:items-stretch">
              <div className="poster-card animate-scale-in shadow-poster max-w-[240px] mx-auto md:mx-0 md:max-w-none md:h-full md:self-stretch">
                <img src={series.poster} alt={pickTitle(series, lang)} className="w-full aspect-[2/3] md:aspect-auto md:h-full object-cover" />
              </div>
              <div className="relative text-white animate-fade-in rounded-2xl border border-white/15 bg-black/55 p-4 md:p-6 shadow-2xl backdrop-blur-md">
                <SeriesBadgeBar
                  slug={series.slug}
                  threadId={`series-${series.slug}`}
                  isNew={!!series.isNew}
                  isTrending={!!series.isTrending}
                  lang={lang}
                />
                <h1 className="text-3xl md:text-5xl font-extrabold mt-3 drop-shadow-lg">{pickTitle(series, lang)}</h1>
                <p className="text-base text-white/80 mt-1">{series.titleEn}</p>
                <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
                  <span className="flex items-center gap-1.5 font-bold"><Star className="h-4 w-4 fill-[var(--rating)] text-[var(--rating)]" /> {series.rating}/10</span>
                  <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {series.firstAirDate ? formatDate(series.firstAirDate, lang) : series.year}</span>
                  {pickDuration(series, lang) && (
                    <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {pickDuration(series, lang)}</span>
                  )}
                  {pickSeasonInfo(series, lang) && (
                    <span className="flex items-center gap-1.5"><Tv className="h-4 w-4" /> {pickSeasonInfo(series, lang)}</span>
                  )}
                  {pickStatus(series, lang) && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 border border-white/20">
                      {UI.status}: {pickStatus(series, lang)}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {pickGenres(series, lang).map((g: string) => (
                    <span key={g} className="text-xs px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white backdrop-blur">{g}</span>
                  ))}
                </div>
                <p className="mt-4 text-white/85 max-w-3xl leading-relaxed">{pickOverview(series, lang)}</p>
                {/* Meta — release dates / director / cast */}
                <dl className="mt-5 grid sm:grid-cols-2 gap-3 text-sm font-light">
                  {series.firstAirDate && (
                    <div className="flex gap-2"><dt className="text-white/60 min-w-24">{UI.firstAir}:</dt><dd className="text-white/90">{formatDate(series.firstAirDate, lang)}</dd></div>
                  )}
                  {series.lastAirDate && (
                    <div className="flex gap-2"><dt className="text-white/60 min-w-24">{UI.lastAir}:</dt><dd className="text-white/90">{formatDate(series.lastAirDate, lang)}</dd></div>
                  )}
                  {series.director && (
                    <div className="flex gap-2"><dt className="text-white/60 min-w-24">{UI.director}:</dt><dd className="text-white/90">{series.director}</dd></div>
                  )}
                  {series.cast && series.cast.length > 0 && (
                    <div className="flex gap-2"><dt className="text-white/60 min-w-24">{UI.cast}:</dt><dd className="text-white/90">{series.cast.join("، ")}</dd></div>
                  )}
                </dl>
                {/* Actions */}
                <SeriesActions series={series} firstEpisode={firstEpisode} t={t} lang={lang} onTrailer={() => setTrailerOpen((v) => !v)} trailerOpen={trailerOpen} />
              </div>
            </div>
          </div>
        </section>

        {/* مشغّل التريلر — يُحقن داخل صفحة المسلسل عند الضغط على زر التريلر */}
        {trailerOpen && series.trailer && (
          <section className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xl sm:text-2xl font-extrabold flex items-center gap-2">
                <Play className="h-5 w-5 text-primary fill-current" />
                 {t("مشاهدة التريلر", "Watch trailer", "Voir la bande-annonce")}
              </h2>
              <button onClick={() => setTrailerOpen(false)} className="text-sm text-muted-foreground hover:text-primary">
                {t("\u0625\u063a\u0644\u0627\u0642", "Close", "Fermer")}
              </button>
            </div>
            <RawanPlayer url={series.trailer} poster={series.backdrop} title={pickTitle(series, lang)} ads={false} />
          </section>
        )}


        {/* Episodes */}
        <section id="watch" style={{ scrollMarginTop: "96px" }} className="container mx-auto px-4 py-10">
          <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 shadow-card">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <div className="flex items-center gap-3">
                <div className="grid place-items-center w-10 h-10 rounded-xl" style={{ background: "var(--gradient-primary)" }}>
                  <Film className="h-5 w-5 text-primary-foreground" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-extrabold">{t("\u0627\u0644\u062d\u0644\u0642\u0627\u062a", "Episodes", "\u00c9pisodes")}</h2>
                <p className="text-xs text-muted-foreground">{displayEpisodes.length} {t("حلقة منشورة", "published", "publiés")}</p>
                </div>
              </div>
              {seasons.length > 1 && (
                <div className="flex flex-wrap gap-2">
                  {seasons.map((s, idx) => (
                    <button
                      key={s}
                      onClick={() => setActiveSeason(s)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-bold border transition ${
                        s === currentSeason
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background border-border hover:bg-accent"
                      }`}
                      aria-label={`${t("\u0627\u0644\u0645\u0648\u0633\u0645", "Season", "Saison")} ${s}`}
                    >
                      {idx === 0 ? `${t("\u0627\u0644\u0645\u0648\u0633\u0645", "Season", "Saison")} ${s}` : s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {displayEpisodes.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-12 border border-dashed border-border rounded-xl">
                {t("لم تُضف أي حلقة بعد. يمكن للمدير الإضافة من لوحة التحكم.", "No episodes yet. Admin can add from dashboard.", "Aucun épisode pour le moment. L’administrateur peut en ajouter depuis le tableau de bord.")}
              </div>
            ) : (
              <>
              <ul className="grid gap-3 grid-cols-1 md:grid-cols-1 lg:grid-cols-4">
                {pagedEps.map((ep) => (
                  <li
                    key={ep.id}
                    className="rounded-xl bg-background border border-border hover:border-primary transition overflow-hidden
                               grid grid-cols-[auto_minmax(0,1fr)] gap-3 p-3
                               md:grid-cols-[auto_minmax(0,1fr)_auto] md:items-center
                               lg:grid-cols-1 lg:gap-0 lg:p-0"
                  >
                    {/* Thumbnail */}
                    <div className="relative w-28 shrink-0 aspect-video rounded-lg overflow-hidden bg-muted
                                    lg:w-full lg:rounded-none">
                      {ep.thumbnail ? (
                        <img src={ep.thumbnail} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full grid place-items-center text-muted-foreground">
                          <Play className="h-6 w-6" />
                        </div>
                      )}
                      <span className="absolute bottom-1 start-1 text-[10px] font-extrabold px-1.5 py-0.5 rounded text-primary-foreground bg-black/70">
                        E{ep.number}
                      </span>
                    </div>

                    {/* Mobile: title+S/E row on top, buttons on bottom — stretched to image height.
                        md+: only title block here. */}
                    <div className="min-w-0 self-stretch flex flex-col justify-between gap-2 md:gap-0 lg:px-3 lg:py-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-bold truncate min-w-0">{ep.title}</div>
                        <div className="text-xs text-muted-foreground shrink-0">
                          S{ep.season} · E{ep.number}
                        </div>
                      </div>
                      {/* Buttons (below title) — compact, fits any width. Hidden on real desktops via JS guard. */}
                      {isHandheld && (
                      <div className="flex gap-2">
                        <Link
                          to="/watch/$slug/$ep"
                          params={{ slug: series.slug, ep: `s${ep.season}e${ep.number}` }}
                          onClick={() => { import("@/lib/internal-nav").then(m => m.allowNextInternalNav()); }}
                          className="btn-primary inline-flex items-center gap-1.5 !py-2 !px-3 text-xs whitespace-nowrap flex-1 justify-center"
                        >
                          <Play className="h-3.5 w-3.5 fill-current" /> <span>{t("شاهد", "Watch", "Regarder")}</span>
                        </Link>

                        <Link
                          to="/download/$slug/$ep"
                          params={{ slug: series.slug, ep: `S${ep.season}E${String(ep.number).padStart(2, "0")}` }}
                          aria-label={t("\u062a\u062d\u0645\u064a\u0644", "Download", "T\u00e9l\u00e9charger")}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary/10 hover:bg-primary hover:text-primary-foreground text-primary border border-primary/30 transition text-xs font-semibold whitespace-nowrap flex-1 justify-center"
                        >
                          <Download className="h-3.5 w-3.5" /> <span>{t("\u062a\u062d\u0645\u064a\u0644", "Download", "T\u00e9l\u00e9charger")}</span>
                        </Link>
                      </div>
                      )}
                    </div>

                  </li>
                ))}
              </ul>
              {totalEpPages > 1 && (
                <nav
                  className="mt-5 flex flex-wrap items-center justify-center gap-2"
                  aria-label={t("تصفح الحلقات", "Episodes pagination", "Pagination des épisodes")}
                >
                  <button
                    type="button"
                    onClick={() => goToEpPage(safeEpPage - 1)}
                    disabled={safeEpPage === 1}
                    className="px-3 py-1.5 rounded-lg text-sm font-bold border border-border bg-background hover:bg-accent disabled:opacity-40 disabled:pointer-events-none"
                  >
                    {t("السابق", "Prev", "Préc.")}
                  </button>
                  {pageNumbers.map((p, i) =>
                    p === "…" ? (
                      <span key={`e-${i}`} className="px-2 text-sm text-muted-foreground">…</span>
                    ) : (
                      <button
                        key={p}
                        type="button"
                        onClick={() => goToEpPage(p as number)}
                        aria-current={p === safeEpPage ? "page" : undefined}
                        className={`min-w-9 px-3 py-1.5 rounded-lg text-sm font-bold border transition ${
                          p === safeEpPage
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background border-border hover:bg-accent"
                        }`}
                      >
                        {p}
                      </button>
                    ),
                  )}
                  <button
                    type="button"
                    onClick={() => goToEpPage(safeEpPage + 1)}
                    disabled={safeEpPage === totalEpPages}
                    className="px-3 py-1.5 rounded-lg text-sm font-bold border border-border bg-background hover:bg-accent disabled:opacity-40 disabled:pointer-events-none"
                  >
                    {t("التالي", "Next", "Suiv.")}
                  </button>
                </nav>
              )}
              </>
            )}
          </div>
        </section>

        <div className="container mx-auto px-4"><AdSlot slot="series_below_episodes" format="horizontal" /></div>

        {/* Rich article — with sticky social + comments sidebar on desktop */}
        <ArticleRenderer
          article={article}
          lang={lang}
          rightColumn={
            isHandheld ? undefined : (
            <ArticleSidebar
              lang={lang}
              threadId={`series-${series.slug}`}
              title={pickTitle(series, lang)}
            />
            )
          }
        />

        {/* Comments below the article on mobile/tablet */}
        {isHandheld && (
          <div>
            <CommentsSection
            threadId={`series-${series.slug}`}
            title={pickTitle(series, lang)}
          />
          </div>
        )}


        {/* Same-country recommendations */}
        {sameCountry.length > 0 && countryEntry && (
          <section className="container mx-auto px-4 pb-12">
            <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
              <h2 className="text-xl sm:text-2xl font-extrabold">
                <span aria-hidden className="me-2">{countryEntry.flag}</span>
                {lang === "ar" ? `مسلسلات ${countryEntry.ar} أخرى` : lang === "fr" ? `Autres séries ${countryEntry.fr}` : `More ${countryEntry.en} Series`}
              </h2>
              <Link
                to="/series"
                search={{ country: countryEntry.code }}
                className="text-sm font-semibold text-primary hover:underline"
              >
                {t("عرض الكل", "View all", "Tout voir")} →
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
              {sameCountry.map((m) => <PosterCard key={m.id} movie={m} />)}
            </div>
          </section>
        )}

        {/* Related */}
        <section className="container mx-auto px-4 py-8">
          <h2 className="text-xl sm:text-2xl font-extrabold mb-4">{t("مسلسلات أخرى", "More series", "Autres séries")}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
            {related.map((m) => <PosterCard key={m.id} movie={m} />)}
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}




/* ---------- SeriesActions: watch first / trailer / favorite / watchlist / share ---------- */
function SeriesActions({
  series,
  firstEpisode,
  t,
  lang,
  onTrailer,
  trailerOpen,
}: {
  series: { slug: string; type: "movie" | "series"; title: string; titleEn: string; titleFr?: string; trailer: string };
  firstEpisode?: { season: number; number: number };
  t: (ar: string, en: string, fr?: string) => string;
  lang: LanguageCode;
  onTrailer?: () => void;
  trailerOpen?: boolean;
}) {
  useFavorites();
  useWatchlist();
  const fav = isFavorite(series.slug);
  const saved = isOnWatchlist(series.slug);
  const [shared, setShared] = useState(false);
  const isHandheld = useIsHandheld();

  async function share() {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const url = `${origin}/series/${series.slug}?lang=${lang}`;
    const shareTitle = lang === "ar" ? series.title : lang === "fr" ? (series.titleFr || series.titleEn) : series.titleEn;
    try {
      if (typeof navigator !== "undefined" && (navigator as Navigator).share) {
        await (navigator as Navigator).share({ title: shareTitle, text: shareTitle, url });
      } else if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        setShared(true);
        setTimeout(() => setShared(false), 1500);
      }
    } catch { /* user cancelled — silent */ }
  }

  const baseBtn =
    "inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold border transition whitespace-nowrap";

  return (
    <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-6">
      {firstEpisode && isHandheld && (
        <Link
          to="/watch/$slug/$ep"
          params={{ slug: series.slug, ep: `s${firstEpisode.season}e${firstEpisode.number}` }}
          onClick={() => { import("@/lib/internal-nav").then(m => m.allowNextInternalNav()); }}
          className="btn-primary inline-flex items-center gap-1.5 sm:gap-2 !px-3 sm:!px-5 !py-1.5 sm:!py-2.5 text-xs sm:text-sm"
        >
          <Play className="h-3.5 w-3.5 sm:h-4 sm:w-4 fill-current" />
          <span>{t("شاهد", "Watch", "Regarder")}</span>
        </Link>
      )}
      {series.trailer && (
        <button
          type="button"
          onClick={onTrailer}
          aria-pressed={!!trailerOpen}
          className={`${baseBtn} bg-card text-foreground border-border hover:bg-accent`}
        >
          <Play className="h-3.5 w-3.5 sm:h-4 sm:w-4 fill-current" />
          <span>{trailerOpen ? t("إخفاء التريلر", "Hide trailer", "Masquer la bande-annonce") : t("التريلر", "Trailer", "Bande-annonce")}</span>
        </button>
      )}
      <button
        type="button"
        onClick={() => toggleFavorite(series.slug, series.type)}
        aria-pressed={fav}
        className={`${baseBtn} ${
          fav
            ? "bg-primary text-primary-foreground border-primary"
            : "bg-card text-foreground border-border hover:bg-accent"
        }`}
      >
        <Heart className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${fav ? "fill-current" : ""}`} />
        <span className="hidden sm:inline">{t(fav ? "في المفضلة" : "المفضلة", fav ? "Favorited" : "Favorite", fav ? "Dans les favoris" : "Favori")}</span>
      </button>
      <button
        type="button"
        onClick={() => toggleWatchlist(series.slug, series.type)}
        aria-pressed={saved}
        aria-label={t(saved ? "محفوظ" : "احفظ", saved ? "Saved" : "Save", saved ? "Enregistré" : "Enregistrer")}
        className={`${baseBtn} ${
          saved
            ? "bg-primary text-primary-foreground border-primary"
            : "bg-card text-foreground border-border hover:bg-accent"
        }`}
      >
        <Bookmark className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${saved ? "fill-current" : ""}`} />
        <span className="hidden sm:inline">{t(saved ? "محفوظ" : "احفظ", saved ? "Saved" : "Save", saved ? "Enregistré" : "Enregistrer")}</span>
      </button>
      <button
        type="button"
        onClick={share}
        aria-label={t("شارك", "Share", "Partager")}
        className={`${baseBtn} bg-card text-foreground border-border hover:bg-accent`}
      >
        {shared ? (
          <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
        ) : (
          <Share2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        )}
        <span className="hidden sm:inline">{shared ? t("تم النسخ", "Copied", "Copié") : t("شارك", "Share", "Partager")}</span>
      </button>

    </div>
  );
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(n >= 10_000 ? 0 : 1) + "K";
  return String(n);
}

function SeriesBadgeBar({
  slug, threadId, isNew, isTrending, lang,
}: {
  slug: string;
  threadId: string;
  isNew: boolean;
  isTrending: boolean;
  lang: LanguageCode;
}) {
  const views = useViews(slug);
  const comments = useCommentsCount(threadId);
  const U = UI_T[lang];
  const pill =
    "inline-flex items-center justify-center gap-1.5 whitespace-nowrap h-7 px-3 rounded-full text-[11px] font-bold border backdrop-blur shadow-lg";
  return (
    <div className="flex flex-row flex-wrap gap-2 items-center">
      <span className={`${pill} bg-primary text-primary-foreground border-primary/60`}>
        <Tv className="h-3.5 w-3.5 shrink-0" />
        {U.series}
      </span>
      {isTrending && (
        <span className={`${pill} bg-[var(--rating)]/15 text-[var(--rating)] border-[var(--rating)]/50`} title={U.trending}>
          <Flame className="h-3.5 w-3.5 shrink-0" />
          {U.trending}
        </span>
      )}
      {isNew && (
        <span className={`${pill} bg-emerald-500/15 text-emerald-300 border-emerald-400/50`} title={U.newBadge}>
          <Sparkles className="h-3.5 w-3.5 shrink-0" />
          {U.newBadge}
        </span>
      )}
      <span className={`${pill} bg-black/70 text-white border-white/20`} title={U.views}>
        <Eye className="h-3.5 w-3.5 shrink-0" /> {formatCount(views)}
      </span>
      <span className={`${pill} bg-black/70 text-white border-white/20`} title={U.comments}>
        <MessageSquare className="h-3.5 w-3.5 shrink-0" /> {formatCount(comments)}
      </span>
    </div>
  );
}

/* ---------- Pagination helper: 1 … p-1 p p+1 … N ---------- */
function buildPageNumbers(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "…")[] = [];
  const add = (v: number | "…") => pages.push(v);
  add(1);
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);
  if (left > 2) add("…");
  for (let i = left; i <= right; i++) add(i);
  if (right < total - 1) add("…");
  add(total);
  return pages;
}
